import { useState, useEffect, useCallback, useRef } from 'react';

import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useTranslation } from '@/i18n';
import { monthlyCutsConceptGenerationService } from '@/api/domains/attendance';
import type { ConceptGenerationProgress } from '@/api/domains/attendance';


// Cadencia de polling del progreso por corte.
const POLLING_INTERVAL_MS = 2000;
// Tiempo que la barra mantiene visible el estado FINAL (100% o error) tras detectar la
// finalización, contado DESDE la finalización (no desde el inicio). Garantiza que el usuario
// vea el llenado al 100% aunque el job haya estado mucho rato en cola antes de completar.
const COMPLETION_HOLD_MS = 1200;

export interface UseConceptGenerationProgressResult {
  progress: ConceptGenerationProgress | null;
  isActive: boolean;
}

export function useConceptGenerationProgress(
  monthlyCutId: number,
  enabled: boolean,
  restartKey: number = 0,
  onFinished?: () => void,
  initialProgressPercentage?: number,
): UseConceptGenerationProgressResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();
  const [progress, setProgress] = useState<ConceptGenerationProgress | null>(null);
  const [isActive, setIsActive] = useState(false);
  const hasErrorRef = useRef(false);
  const intervalRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  // Se guarda en un ref para que cambios de identidad del callback no reinicien el polling.
  const onFinishedRef = useRef(onFinished);
  useEffect(() => { onFinishedRef.current = onFinished; }, [onFinished]);
  // Semilla del % inicial en un ref: se consume solo al (re)arrancar; sus cambios no reinician.
  const initialProgressRef = useRef(initialProgressPercentage);
  useEffect(() => {
    initialProgressRef.current = initialProgressPercentage;
  }, [initialProgressPercentage]);

  const clearPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current !== null) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Detiene el polling y oculta la barra tras mantener el estado final por COMPLETION_HOLD_MS.
  // Al ocultarse, notifica onFinished para que el padre deje de marcar el corte como activo.
  const finish = useCallback(() => {
    clearPolling();
    clearHideTimeout();
    hideTimeoutRef.current = window.setTimeout(() => {
      setIsActive(false);
      hideTimeoutRef.current = null;
      onFinishedRef.current?.();
    }, COMPLETION_HOLD_MS);
  }, [clearPolling, clearHideTimeout]);

  const fetchProgress = useCallback(async () => {
    const signal = createSignal();
    const response = await monthlyCutsConceptGenerationService.getProgress(monthlyCutId, signal);

    if (response.error?.code === 'ABORTED') return;

    if (!response.success || !response.data) {
      if (!hasErrorRef.current) {
        hasErrorRef.current = true;
        showApiError(
          response,
          translate('attendance:monthlyCutsList.conceptGeneration.errors.loadProgress'),
        );
      }
      return;
    }

    hasErrorRef.current = false;
    setProgress(response.data);

    // Estado terminal: el proceso terminó (correctamente o con error). Mientras solo esté
    // inactivo pero sin completar (job aún no arrancó), se mantiene el polling y la barra.
    if (response.data.isCompleted || response.data.hasError) {
      finish();
    }
  }, [createSignal, monthlyCutId, showApiError, translate, finish]);

  useEffect(() => {
    if (!enabled) {
      clearPolling();
      clearHideTimeout();
      setIsActive(false);
      return;
    }

    // Arranque optimista: mostrar la barra y empezar a pollear. Si el listado ya reporta un avance
    // (generación en curso al cargar / otro usuario), se siembra ese %; si no (p. ej. re-disparo en
    // sesión), se parte de null para no reflejar un 100% viejo del run anterior.
    hasErrorRef.current = false;
    const seed = initialProgressRef.current;
    setProgress(
      seed != null
        ? {
          monthlyCutId,
          progressPercentage: seed,
          isActive: true,
          isCompleted: false,
          hasError: false,
          errorMessage: null,
        }
        : null,
    );
    setIsActive(true);
    void fetchProgress();
    intervalRef.current = window.setInterval(() => void fetchProgress(), POLLING_INTERVAL_MS);

    return () => {
      clearPolling();
      clearHideTimeout();
    };
    // `restartKey` se incluye a propósito: al cambiar, reinicia el efecto (re-arranca el polling).
    // `monthlyCutId` es estable por fila; cambia junto con `fetchProgress`, no añade re-arranques.
  }, [enabled, restartKey, monthlyCutId, fetchProgress, clearPolling, clearHideTimeout]);

  return { progress, isActive };
}

export default useConceptGenerationProgress;
